import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const colors = {
  1: { text: 'text-violet-300', bgSelected: 'bg-violet-500/10', borderSelected: 'border-violet-500', shadowSelected: 'shadow-violet-500/20', buttonBg: 'bg-violet-600 hover:bg-violet-500', iconBg: 'bg-violet-500/20 text-violet-400' },
  2: { text: 'text-amber-300', bgSelected: 'bg-amber-500/10', borderSelected: 'border-amber-500', shadowSelected: 'shadow-amber-500/20', buttonBg: 'bg-amber-600 hover:bg-amber-500', iconBg: 'bg-amber-500/20 text-amber-400' },
  3: { text: 'text-fuchsia-300', bgSelected: 'bg-fuchsia-500/10', borderSelected: 'border-fuchsia-500', shadowSelected: 'shadow-fuchsia-500/20', buttonBg: 'bg-fuchsia-600 hover:bg-fuchsia-500', iconBg: 'bg-fuchsia-500/20 text-fuchsia-400' },
  4: { text: 'text-emerald-300', bgSelected: 'bg-emerald-500/10', borderSelected: 'border-emerald-500', shadowSelected: 'shadow-emerald-500/20', buttonBg: 'bg-emerald-600 hover:bg-emerald-500', iconBg: 'bg-emerald-500/20 text-emerald-400' }
};

const AddCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialEditId, setInitialEditId] = useState(location.state?.editCategoryId || null); 

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);
  const [selectedL4, setSelectedL4] = useState(null);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null); 
  const [saving, setSaving] = useState(false);

  // Delete Confirm State
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
      setCategories(res.data);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialEditId && categories.length > 0) {
      const target = categories.find(c => c.id === Number(initialEditId));
      if (target) {
        if (target.level === 1) setSelectedL1(target.id);
        if (target.level === 2) { setSelectedL2(target.id); setSelectedL1(target.parent_id); }
        if (target.level === 3) {
          setSelectedL3(target.id);
          setSelectedL2(target.parent_id);
          const parentL2 = categories.find(c => c.id === target.parent_id);
          if (parentL2) setSelectedL1(parentL2.parent_id);
        }
        if (target.level === 4) {
          setSelectedL4(target.id);
          setSelectedL3(target.parent_id);
          const parentL3 = categories.find(c => c.id === target.parent_id);
          if (parentL3) {
            setSelectedL2(parentL3.parent_id);
            const parentL2 = categories.find(c => c.id === parentL3.parent_id);
            if (parentL2) setSelectedL1(parentL2.parent_id);
          }
        }
        
        setEditData({ ...target, parent_id: target.parent_id });
        setModalOpen(true);
        
        // Clear so it doesn't trigger again
        setInitialEditId(null);
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [initialEditId, categories, navigate, location.pathname]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editData.name.trim()) return toast.error('Name is required');
    
    setSaving(true);
    try {
      const payload = {
        name: editData.name.trim(),
        level: Number(editData.level),
        parent_id: editData.parent_id ? Number(editData.parent_id) : null,
        sort_order: Number(editData.sort_order || 0),
        profit_perc: editData.profit_perc !== '' && editData.profit_perc !== null && editData.profit_perc !== undefined ? Number(editData.profit_perc) : null
      };

      if (editData.id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/categories/${editData.id}`, payload);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/categories`, payload);
        toast.success('Category created successfully');
      }
      setModalOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(editData.id ? 'Failed to update' : 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/categories/${confirmDelete.id}`);
      toast.success('Deleted successfully!');
      
      if (selectedL1 === confirmDelete.id) { setSelectedL1(null); setSelectedL2(null); setSelectedL3(null); setSelectedL4(null); }
      if (selectedL2 === confirmDelete.id) { setSelectedL2(null); setSelectedL3(null); setSelectedL4(null); }
      if (selectedL3 === confirmDelete.id) { setSelectedL3(null); setSelectedL4(null); }
      if (selectedL4 === confirmDelete.id) { setSelectedL4(null); }

      setConfirmDelete(null);
      fetchCategories();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const l1Items = categories.filter(c => c.level === 1).sort((a,b) => a.sort_order - b.sort_order);
  const l2Items = selectedL1 ? categories.filter(c => c.level === 2 && c.parent_id === selectedL1).sort((a,b) => a.sort_order - b.sort_order) : [];
  const l3Items = selectedL2 ? categories.filter(c => c.level === 3 && c.parent_id === selectedL2).sort((a,b) => a.sort_order - b.sort_order) : [];
  const l4Items = selectedL3 ? categories.filter(c => c.level === 4 && c.parent_id === selectedL3).sort((a,b) => a.sort_order - b.sort_order) : [];

  const renderPane = (level, title, items, selectedId, onSelect, parentId) => {
    const c = colors[level];
    
    return (
      <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-[calc(100vh-200px)] min-h-[450px]">
        <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex justify-between items-center">
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h3>
            <p className="text-xs text-slate-400 mt-1">{items.length} items</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
          {items.length === 0 ? (
             <div className="text-center py-10 text-slate-500 text-xs italic">No items yet</div>
          ) : items.map(item => {
            const isSelected = selectedId === item.id;
            return (
              <div 
                key={item.id} 
                onClick={() => onSelect && onSelect(item.id)} 
                className={`cursor-pointer p-3.5 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? `${c.bgSelected} ${c.borderSelected} ${c.shadowSelected}` : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h4 className={`font-bold text-sm truncate ${isSelected ? c.text : 'text-slate-200'}`}>{item.name}</h4>
                  <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); setEditData({...item, parent_id: parentId}); setModalOpen(true); }} className="text-blue-400 hover:text-blue-300 p-1.5 bg-blue-500/10 rounded-lg hover:bg-blue-500/20 transition">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(item); }} className="text-rose-500/80 hover:text-rose-400 p-1.5 bg-rose-500/10 rounded-lg hover:bg-rose-500/20 transition">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 bg-slate-800/50 border-t border-slate-700">
          <button 
            onClick={() => { setEditData({ id: null, level, parent_id: parentId, name: '', sort_order: 0, profit_perc: '' }); setModalOpen(true); }} 
            className={`w-full text-white text-xs font-bold py-3 rounded-xl transition flex items-center justify-center gap-2 shadow-sm ${c.buttonBg}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
            New {title.split('(')[0].trim()}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Category Builder" subtitle="Visual hierarchy manager">
      <Toaster position="top-right" />
      <div className="max-w-[1400px] mx-auto pb-8">
        
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            </div>
            <div>
              <h2 className="text-2xl font-black text-white uppercase">Category Hierarchy</h2>
              <p className="text-xs text-slate-400">Build your structure layer by layer</p>
            </div>
          </div>
          <Link to="/category-master" className="bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 px-5 rounded-xl transition flex items-center gap-2 text-sm border border-slate-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to List
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-20 bg-slate-900/60 border border-slate-700 rounded-2xl backdrop-blur-sm shadow-xl">
            <div className="w-8 h-8 rounded-full border-4 border-emerald-500/20 border-t-emerald-400 animate-spin mx-auto mb-3" />
            <p className="text-emerald-400 font-bold text-sm">Loading Categories...</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4 w-full custom-scrollbar items-start">
            {renderPane(1, 'LEVEL 1 (MAIN SECTION)', l1Items, selectedL1, id => { setSelectedL1(id); setSelectedL2(null); setSelectedL3(null); setSelectedL4(null); }, null)}
            
            {selectedL1 && renderPane(2, 'LEVEL 2 (CATEGORY)', l2Items, selectedL2, id => { setSelectedL2(id); setSelectedL3(null); setSelectedL4(null); }, selectedL1)}
            
            {selectedL2 && renderPane(3, 'LEVEL 3 (SUB CATEGORY)', l3Items, selectedL3, id => { setSelectedL3(id); setSelectedL4(null); }, selectedL2)}

            {selectedL3 && renderPane(4, 'LEVEL 4 (ITEM)', l4Items, selectedL4, id => { setSelectedL4(id); }, selectedL3)}
          </div>
        )}
      </div>

      <style>{`.custom-scrollbar::-webkit-scrollbar { height: 8px; width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>

      {/* MODAL */}
      {modalOpen && editData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className={`bg-slate-900 border ${colors[editData.level].borderSelected}/50 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden`}>
            <div className={`${colors[editData.level].bgSelected} border-b ${colors[editData.level].borderSelected}/20 px-6 py-5 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full ${colors[editData.level].iconBg} flex items-center justify-center`}>
                  {editData.id ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg> : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>}
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">{editData.id ? 'Edit Category' : 'New Category'}</h3>
                  <p className="text-xs text-slate-400">Level {editData.level}</p>
                </div>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Name *</label>
                <input type="text" value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} autoFocus className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition placeholder-slate-600" placeholder="Enter name" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Sort Order</label>
                <input type="number" value={editData.sort_order} onChange={e => setEditData(p => ({ ...p, sort_order: e.target.value }))} className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition font-mono" />
              </div>

              {editData.level === 3 && (
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Profit %</label>
                  <input type="number" step="0.5" min="0" max="100" value={editData.profit_perc} onChange={e => setEditData(p => ({ ...p, profit_perc: e.target.value }))} placeholder="e.g. 20" className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition font-mono" />
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3.5 rounded-xl transition text-sm">Cancel</button>
                <button type="submit" disabled={saving} className={`flex-1 ${colors[editData.level].buttonBg} text-white font-bold py-3.5 rounded-xl transition disabled:opacity-50 text-sm flex justify-center items-center gap-2`}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-rose-400 font-bold text-lg">Delete Category?</h3>
                <p className="text-slate-400 text-xs">This action cannot be undone</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm">Are you sure you want to delete <span className="text-white font-bold">"{confirmDelete.name}"</span>?</p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfirmDelete(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button>
                <button onClick={handleDelete} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default AddCategory;

